import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { toast } from "sonner";

const SiteCtx = createContext(null);
const AuthCtx = createContext(null);
const CartCtx = createContext(null);

export const useSite = () => useContext(SiteCtx);
export const useAuth = () => useContext(AuthCtx);
export const useCart = () => useContext(CartCtx);

function SiteProvider({ children }) {
  const [site, setSite] = useState(null);
  const reload = useCallback(() => api.get("/site").then((r) => setSite(r.data)), []);
  useEffect(() => {
    reload();
    const onVisible = () => {
      if (document.visibilityState === "visible") reload();
    };
    window.addEventListener("site-changed", reload);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("site-changed", reload);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [reload]);
  return <SiteCtx.Provider value={{ site, reload }}>{children}</SiteCtx.Provider>;
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  const signIn = (data) => {
    localStorage.setItem("mps_token", data.token);
    if (data.free_token) localStorage.setItem("mps_free", data.free_token);
    setUser(data.user);
  };
  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("mps_token");
    setUser(false);
  };
  return <AuthCtx.Provider value={{ user, setUser, signIn, logout }}>{children}</AuthCtx.Provider>;
}

function CartProvider({ children }) {
  const [items, setItems] = useState(() => JSON.parse(localStorage.getItem("mps_cart") || "[]"));
  useEffect(() => {
    localStorage.setItem("mps_cart", JSON.stringify(items));
  }, [items]);
  const addProduct = (p) => {
    if (items.some((i) => i.type === "product" && i.product_id === p.id)) return toast("Materiál už máte v košíku");
    setItems([...items, { type: "product", product_id: p.id, name: p.name, price: p.sale_price || p.price, image: p.images?.[0], slug: p.slug, physical: !!p.physical }]);
    toast.success(`${p.name} je v košíku`);
  };
  const addVoucher = (value, qty = 1) => {
    const ex = items.find((i) => i.type === "voucher" && i.value === value);
    if (ex) setItems(items.map((i) => (i === ex ? { ...i, qty: i.qty + qty } : i)));
    else setItems([...items, { type: "voucher", value, qty, name: `Dárkový poukaz ${value} Kč`, price: value }]);
    toast.success(`Poukaz ${value} Kč je v košíku`);
  };
  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
  const setQty = (idx, qty) => setItems(items.map((i, k) => (k === idx ? { ...i, qty: Math.max(1, qty) } : i)));
  const clear = () => setItems([]);
  const syncPhysical = (map) => setItems((cur) => cur.map((i) => (i.type === "product" && i.slug in map ? { ...i, physical: map[i.slug] } : i)));
  const total = items.reduce((s, i) => s + i.price * (i.qty || 1), 0);
  const count = items.reduce((s, i) => s + (i.qty || 1), 0);
  return <CartCtx.Provider value={{ items, addProduct, addVoucher, remove, setQty, clear, syncPhysical, total, count }}>{children}</CartCtx.Provider>;
}

export function Providers({ children }) {
  return (
    <SiteProvider>
      <AuthProvider>
        <CartProvider>{children}</CartProvider>
      </AuthProvider>
    </SiteProvider>
  );
}
